import { useMemo, useState } from "react";

import { availColor } from "../lib/types/demo";
import type {
  DemoTrip, EventsPayload, Incident, SeatEventImpact, SitePayload, WhatIf,
} from "../lib/types/demo";
import type { Selection } from "../lib/selection";


// ---------------------------------------------------------------------------
// Availability-view sidebar bodies: the trip dashboard (no selection), the
// incident drill-down, and the per-seat availability headline. All demo data.
// What-if simulation (from the original LOPA generator UX): uncheck any
// incident to exclude it fleet-wide, or a single event inside a seat card to
// exclude it for that seat only — scores recompute live.
// ---------------------------------------------------------------------------

type SortOrder = "impact" | "time" | "frequency";


export function TripPanel({
  trip, onSelect, whatIf, tripNav,
}: {
  trip: DemoTrip;
  onSelect?: (s: Selection | undefined) => void;
  whatIf?: WhatIf;
  tripNav?: {
    index: number;
    count: number;
    onPrev: () => void;
    onNext: () => void;
    onBackToSite: () => void;
  };
}) {
  const [sort, setSort] = useState<SortOrder>("impact");

  // event_id frequency — powers the "By frequency" sort and the ×N badge.
  const idFreq = useMemo(() => {
    const f: Record<string, number> = {};
    for (const i of trip.incidents) f[i.event_id] = (f[i.event_id] ?? 0) + 1;
    return f;
  }, [trip.incidents]);

  // Keep the ORIGINAL index with each incident — exclusions and incident
  // selection are index-based, so reordering must not lose it.
  const ordered = useMemo(() => {
    const indexed = trip.incidents.map((inc, idx) => ({ inc, idx }));
    if (sort === "time") {
      indexed.sort((a, b) => a.inc.event_time.localeCompare(b.inc.event_time));
    } else if (sort === "frequency") {
      indexed.sort(
        (a, b) =>
          idFreq[b.inc.event_id] - idFreq[a.inc.event_id] ||
          b.inc.impact - a.inc.impact,
      );
    } else {
      indexed.sort((a, b) => b.inc.impact - a.inc.impact);
    }
    return indexed;
  }, [trip.incidents, sort, idFreq]);

  const distinctAffected = useMemo(() => {
    const s = new Set<string>();
    for (const i of trip.incidents) i.seats.forEach((x) => s.add(x));
    return s.size;
  }, [trip.incidents]);

  return (
    <div className="space-y-4">
      {/* Trip navigator: step through the 30-trip window without leaving
          the deep dive; jump back to the site rollup in one click. */}
      {tripNav && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={tripNav.onPrev}
            disabled={tripNav.index <= 0}
            aria-label="Previous trip"
            className="rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-30"
          >
            ‹
          </button>
          <span className="text-[11px] font-semibold tabular-nums text-slate-500">
            trip {tripNav.index + 1} / {tripNav.count}
          </span>
          <button
            onClick={tripNav.onNext}
            disabled={tripNav.index >= tripNav.count - 1}
            aria-label="Next trip"
            className="rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-30"
          >
            ›
          </button>
          <button
            onClick={tripNav.onBackToSite}
            className="ml-auto text-[11px] font-semibold text-sky-600 hover:text-sky-800"
          >
            ← back to Site view
          </button>
        </div>
      )}

      {/* What-if simulation banner */}
      {whatIf?.active && (
        <div className="rounded-lg border border-sky-300 border-l-4 border-l-sky-500 bg-sky-50 px-3 py-2 text-[11.5px] leading-relaxed text-slate-700">
          <b>Simulation active</b> — excluding {whatIf.excluded.size} of{" "}
          {trip.incidents.length} incidents
          {whatIf.excludedBySeat.size > 0 &&
            ` (+ per-seat exclusions on ${whatIf.excludedBySeat.size} seat${whatIf.excludedBySeat.size === 1 ? "" : "s"})`}
          . Availability recomputed as if these faults did not occur.
          <button
            onClick={whatIf.onReset}
            className="mt-1.5 block rounded bg-sky-500 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-sky-600"
          >
            Reset simulation
          </button>
        </div>
      )}

      {/* Trip headline */}
      <div className="rounded-xl border border-slate-100 bg-gradient-to-b from-white to-slate-50 px-4 py-3 shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Trip
        </p>
        <p className="mt-0.5 text-sm font-bold text-slate-900">{trip.label}</p>
        <p className="text-[11px] text-slate-400">{trip.date}</p>
        <div className="mt-3 flex items-center gap-3">
          <span
            className="text-2xl font-bold tabular-nums tracking-tight"
            style={{ color: availColor(trip.avg_score) }}
          >
            {trip.avg_score.toFixed(1)}%
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, trip.avg_score)}%`,
                background: availColor(trip.avg_score),
              }}
            />
          </div>
        </div>
        <p className="mt-1 text-[10.5px] text-slate-400">
          fleet-average seat availability{whatIf?.active ? " · simulated" : ""}
        </p>
      </div>

      {/* Incidents */}
      <section>
        <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Fault incidents on this trip · {trip.incidents.length}
          {distinctAffected > 0 && (
            <span className="ml-1 font-semibold normal-case tracking-normal text-slate-400">
              — {distinctAffected} distinct seats affected
            </span>
          )}
        </p>
        {trip.incidents.length > 0 && (
          <>
            <div className="mb-1.5 flex items-center gap-1 text-[10.5px]">
              <span className="mr-0.5 text-slate-400">Sort:</span>
              {(["impact", "time", "frequency"] as const).map((o) => (
                <button
                  key={o}
                  onClick={() => setSort(o)}
                  className={
                    "rounded-full px-2 py-0.5 font-semibold transition-colors " +
                    (sort === o
                      ? "bg-slate-800 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                  }
                >
                  By {o}
                </button>
              ))}
            </div>
            {whatIf && (
              <p className="mb-1.5 text-[10.5px] italic text-slate-400">
                Uncheck any incident to see what availability would have been
                without it. Click a card to light up the seats it hit.
              </p>
            )}
          </>
        )}
        <div className="space-y-1.5">
          {ordered.map(({ inc, idx }) => (
            <IncidentCard
              key={`${inc.event_id}-${idx}`}
              inc={inc}
              freq={idFreq[inc.event_id]}
              excluded={whatIf?.excluded.has(idx) ?? false}
              onToggle={whatIf ? () => whatIf.onToggleIncident(idx) : undefined}
              onClick={() => onSelect?.({ kind: "incident", idx })}
            />
          ))}
          {trip.incidents.length === 0 && (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              Clean trip — no fault incidents recorded.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}


function IncidentCard({
  inc, freq, excluded, onToggle, onClick,
}: {
  inc: Incident;
  freq: number;
  excluded: boolean;
  onToggle?: () => void;
  onClick: () => void;
}) {
  const scoring = inc.penalty > 0;
  return (
    <div
      className={
        "group flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors " +
        (excluded
          ? "border-slate-200 border-dashed bg-white opacity-60"
          : "border-slate-100 bg-slate-50 hover:border-slate-200 hover:bg-white hover:shadow-sm")
      }
    >
      {onToggle && (
        <input
          type="checkbox"
          checked={!excluded}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          title={excluded
            ? "Excluded from scoring — re-check to restore"
            : "Uncheck to exclude this incident from scoring (what-if)"}
          className="mt-0.5 shrink-0 cursor-pointer accent-sky-600"
        />
      )}
      <button onClick={onClick} className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-2">
          <span
            className={
              "h-2 w-2 shrink-0 rounded-full " +
              (scoring ? "bg-red-500" : "bg-slate-300")
            }
          />
          <span className={
            "flex-1 truncate text-xs font-semibold text-slate-800" +
            (excluded ? " line-through decoration-slate-400" : "")
          }>
            {inc.description || inc.event_id}
          </span>
          <span className="shrink-0 rounded-full bg-slate-200/70 px-2 py-0.5 text-[10px] font-bold tabular-nums text-slate-600">
            {inc.seat_count} seats
          </span>
        </span>
        <span className="mt-1 flex items-center gap-2 pl-4 text-[10.5px] text-slate-400">
          <span className="font-mono">{inc.event_id}</span>
          {freq > 1 && (
            <span className="rounded bg-amber-100 px-1 font-bold text-amber-700">
              ×{freq}
            </span>
          )}
          <span>·</span>
          <span>{inc.source_lru}</span>
          <span>·</span>
          <span>{formatTime(inc.event_time)}</span>
          {scoring && (
            <span className="ml-auto font-semibold text-red-400">
              −{Math.round(inc.penalty * 100)}% × {inc.duration}m
            </span>
          )}
        </span>
      </button>
    </div>
  );
}


export function IncidentBody({
  trip, idx, onSelect,
}: {
  trip: DemoTrip;
  idx: number;
  onSelect?: (s: Selection | undefined) => void;
}) {
  const inc = trip.incidents[idx];
  if (!inc) {
    return <p className="text-sm text-slate-500">Incident not found on this trip.</p>;
  }
  const scoring = inc.penalty > 0;
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
        <Row k="Event" v={<span className="font-mono">{inc.event_id}</span>} />
        <Row k="Source" v={inc.source_lru} />
        <Row k="Time" v={formatTime(inc.event_time)} />
        <Row k="Duration" v={`${inc.duration} min`} />
        <Row
          k="Penalty"
          v={scoring
            ? <span className="font-semibold text-red-500">{Math.round(inc.penalty * 100)}% per seat</span>
            : <span className="text-slate-500">none (informational)</span>}
        />
        {scoring && (
          <Row k="Impact" v={`${inc.impact.toLocaleString()} seat-minutes`} />
        )}
      </div>

      <section>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Affected seats · {inc.seat_count}
        </p>
        <div className="flex flex-wrap gap-1">
          {inc.seats.map((sid) => (
            <button
              key={sid}
              onClick={() => onSelect?.({ kind: "seat", seatId: sid })}
              className="rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] font-semibold text-slate-700 transition-colors hover:bg-sky-100 hover:text-sky-800"
            >
              {sid}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}


export function SeatAvailability({
  trip, seatId, events, onOpenCatalog, whatIf,
}: {
  trip: DemoTrip;
  seatId: string;
  events?: EventsPayload;
  onOpenCatalog?: () => void;
  whatIf?: WhatIf;
}) {
  const s = trip.seats[seatId];
  if (!s) return null;
  const seatEvents = s.events ?? [];
  const potential = events?.events_by_layer?.seat?.length ?? 0;
  // trip is the SIMULATED trip; footnote the as-recorded score when it moved.
  const actualPct = whatIf?.actualSeats[seatId]?.availability_pct;
  const simulated = whatIf?.active &&
    actualPct !== undefined && actualPct !== s.availability_pct;
  return (
    <div className="mb-5 space-y-3">
      <div className="rounded-xl border border-slate-100 bg-gradient-to-b from-white to-slate-50 px-4 py-3 shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Availability · {trip.label}
        </p>
        <div className="mt-1.5 flex items-center gap-3">
          <span
            className="text-2xl font-bold tabular-nums tracking-tight"
            style={{ color: availColor(s.availability_pct) }}
          >
            {s.availability_pct}%
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, s.availability_pct)}%`,
                background: availColor(s.availability_pct),
              }}
            />
          </div>
        </div>
        {simulated && (
          <p className="mt-1 text-[10.5px] text-slate-400">
            simulated (what-if) · actual as recorded:{" "}
            <b style={{ color: availColor(actualPct) }}>{actualPct}%</b>
          </p>
        )}
      </div>

      <section>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Fault events on this seat · {seatEvents.length}
        </p>
        {seatEvents.length === 0 && (
          <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            No events hit this seat on this trip.
          </p>
        )}
        <div className="space-y-1">
          {seatEvents.map((ev, i) => (
            <SeatEventRow key={i} ev={ev} seatId={seatId} whatIf={whatIf} />
          ))}
        </div>
        {seatEvents.length > 0 && whatIf && (
          <p className="mt-1 text-[10.5px] italic text-slate-400">
            Uncheck an event to exclude it from this seat's score only —
            other seats keep their penalty.
          </p>
        )}
        {potential > 0 && (
          <button
            onClick={onOpenCatalog}
            className="mt-2 text-[11px] font-semibold text-sky-600 hover:text-sky-800"
          >
            {potential} event types could affect this seat — open the catalog →
          </button>
        )}
      </section>
    </div>
  );
}


function SeatEventRow({
  ev, seatId, whatIf,
}: {
  ev: SeatEventImpact;
  seatId: string;
  whatIf?: WhatIf;
}) {
  const scoring = ev.penalty > 0;
  const idx = ev.incident_idx;
  const toggleable = whatIf && idx !== null && idx !== undefined;
  const excludedGlobally = toggleable && whatIf.excluded.has(idx);
  const excludedHere = toggleable &&
    (whatIf.excludedBySeat.get(seatId)?.has(idx) ?? false);
  const isExcluded = Boolean(excludedGlobally || excludedHere);
  const title = excludedGlobally
    ? "Excluded globally — re-check the incident in the trip list to restore"
    : excludedHere
      ? "Excluded for this seat (per-seat what-if)"
      : "Uncheck to exclude this event from this seat's score";
  return (
    <div
      className={
        "flex items-start gap-2 rounded-lg border px-3 py-1.5 " +
        (isExcluded
          ? "border-dashed border-slate-200 bg-white opacity-60"
          : "border-slate-100 bg-slate-50")
      }
    >
      {toggleable && (
        <input
          type="checkbox"
          checked={!isExcluded}
          disabled={Boolean(excludedGlobally)}
          onChange={() => whatIf.onToggleSeatEvent(seatId, idx)}
          title={title}
          className="mt-0.5 shrink-0 cursor-pointer accent-sky-600 disabled:cursor-not-allowed"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 text-[11.5px] font-semibold text-slate-800">
          <span
            className={
              "h-1.5 w-1.5 shrink-0 rounded-full " +
              (scoring ? "bg-red-500" : "bg-slate-300")
            }
          />
          <span className={
            "flex-1 truncate" +
            (isExcluded ? " line-through decoration-slate-400" : "")
          }>
            {ev.description || ev.event_id}
          </span>
          {scoring && (
            <span className="shrink-0 font-bold tabular-nums text-red-500">
              −{Math.round(ev.penalty * 100)}%
            </span>
          )}
        </p>
        <p className="pl-3.5 font-mono text-[10px] text-slate-400">
          {ev.event_id} · {ev.source_lru} · {formatTime(ev.event_time)} · {ev.duration}m
        </p>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Site sub-mode: 30-day per-tail rollup. The LOPA recolours by each seat's
// window-average score; this panel carries the analytics.
// ---------------------------------------------------------------------------

export function SitePanel({
  site, onSelect, onTripSelect, onEventSearch,
}: {
  site: SitePayload;
  onSelect?: (s: Selection | undefined) => void;
  /** Deep-dive: trend bars, worst-trip rows, best/worst stat tiles. */
  onTripSelect?: (tripId: string) => void;
  /** Opens the event catalog pre-searched to an event code. */
  onEventSearch?: (query?: string) => void;
}) {
  const hotspots = useMemo(
    () =>
      Object.entries(site.seatFaultCount)
        .filter(([, n]) => n > 0)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 10),
    [site.seatFaultCount],
  );
  const maxHist = Math.max(1, ...site.scoreHistogram.map((h) => h.n));

  return (
    <div className="space-y-4">
      {/* Window headline */}
      <div className="rounded-xl border border-slate-100 bg-gradient-to-b from-white to-slate-50 px-4 py-3 shadow-sm">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          Site · {site.tailId}
        </p>
        <div className="mt-1.5 flex items-center gap-3">
          <span
            className="text-2xl font-bold tabular-nums tracking-tight"
            style={{ color: availColor(site.windowAvg) }}
          >
            {site.windowAvg.toFixed(1)}%
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(100, site.windowAvg)}%`,
                background: availColor(site.windowAvg),
              }}
            />
          </div>
        </div>
        <p className="mt-1 text-[10.5px] text-slate-400">
          {site.windowDays}-day window · {site.windowStart} → {site.windowEnd}
        </p>
        <div className="mt-2 grid grid-cols-3 gap-2 border-t border-slate-100 pt-2 text-center">
          <SiteStat label="worst trip" value={`${site.worstTripAvg}%`}
                    color={availColor(site.worstTripAvg)}
                    onClick={onTripSelect && site.worstTripId
                      ? () => onTripSelect(site.worstTripId!) : undefined} />
          <SiteStat label="best trip" value={`${site.bestTripAvg}%`}
                    color={availColor(site.bestTripAvg)}
                    onClick={onTripSelect && site.bestTripId
                      ? () => onTripSelect(site.bestTripId!) : undefined} />
          <SiteStat label="incidents" value={String(site.totalIncidents)} />
        </div>
      </div>

      {/* Trend — one bar per trip; every bar deep-dives into its trip. */}
      <section>
        <SectionLabel>Availability trend · {site.trend.length} trips</SectionLabel>
        <div className="flex h-16 items-end gap-[2px] rounded-lg border border-slate-100 bg-slate-50 px-2 pb-1 pt-2">
          {site.trend.map((t) => (
            <button
              key={t.tripId}
              onClick={onTripSelect ? () => onTripSelect(t.tripId) : undefined}
              title={`${t.date} · ${t.avg}% · ${t.nIncidents} incident${t.nIncidents === 1 ? "" : "s"} · ${t.tripId} — click to deep-dive`}
              className={
                "min-w-[3px] flex-1 rounded-sm transition-transform " +
                (onTripSelect
                  ? "cursor-pointer hover:-translate-y-0.5 hover:ring-2 hover:ring-slate-400/60"
                  : "")
              }
              style={{
                // Zoom into the interesting band: 70–100% spans the bar area.
                height: `${Math.max(8, ((t.avg - 70) / 30) * 100)}%`,
                background: availColor(t.avg),
              }}
            />
          ))}
        </div>
        <p className="mt-0.5 flex justify-between text-[9.5px] text-slate-400">
          <span>{site.windowStart}</span>
          {onTripSelect && (
            <span className="italic">click a bar to open that trip</span>
          )}
          <span>{site.windowEnd}</span>
        </p>
      </section>

      {/* Score distribution */}
      <section>
        <SectionLabel>Trip-score distribution</SectionLabel>
        <div className="space-y-0.5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
          {site.scoreHistogram.map((h) => (
            <div key={h.label} className="flex items-center gap-2">
              <span className="w-12 text-right text-[10px] tabular-nums text-slate-500">
                {h.label}
              </span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-sm bg-slate-200/50">
                <div
                  className="h-full rounded-sm bg-slate-400"
                  style={{ width: `${(h.n / maxHist) * 100}%` }}
                />
              </div>
              <span className="w-5 text-[10px] font-semibold tabular-nums text-slate-500">
                {h.n}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Top recurring events — click to open the catalog on that code. */}
      <section>
        <SectionLabel>Top events in window</SectionLabel>
        <div className="space-y-1">
          {site.topEvents.slice(0, 6).map((e) => (
            <button
              key={e.eventId}
              onClick={onEventSearch ? () => onEventSearch(e.eventId) : undefined}
              title={onEventSearch ? "Open the event catalog on this code" : undefined}
              className={
                "block w-full rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 text-left " +
                (onEventSearch
                  ? "transition-colors hover:border-slate-200 hover:bg-white hover:shadow-sm"
                  : "")
              }
            >
              <p className="flex items-center gap-2 text-[11.5px]">
                <span className="font-mono font-bold text-slate-800">{e.eventId}</span>
                <span
                  className={
                    "rounded-full px-1.5 py-0.5 text-[9px] font-bold " +
                    (e.score === "Y"
                      ? "bg-red-100 text-red-600"
                      : "bg-slate-200 text-slate-500")
                  }
                >
                  {e.score === "Y" ? "SCORING" : "INFO"}
                </span>
                <span className="ml-auto font-bold tabular-nums text-slate-700">
                  ×{e.count}
                </span>
              </p>
              <p className="mt-0.5 text-[10px] text-slate-400">
                {e.tripsAffected} trips · {e.seatsAffected} seats · {e.penalty}
              </p>
            </button>
          ))}
        </div>
      </section>

      {/* Worst trips — click a row to deep-dive into that trip. */}
      <section>
        <SectionLabel>Worst trips</SectionLabel>
        <div className="space-y-1">
          {site.worstTrips.map((t) => (
            <button
              key={t.tripId}
              onClick={onTripSelect ? () => onTripSelect(t.tripId) : undefined}
              className={
                "flex w-full items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-1.5 text-left text-[11px] " +
                (onTripSelect
                  ? "transition-colors hover:border-slate-200 hover:bg-white hover:shadow-sm"
                  : "")
              }
            >
              <span className="font-mono font-semibold text-slate-700">{t.tripId}</span>
              <span className="text-slate-400">{t.date}</span>
              <span className="ml-auto text-slate-400">
                {t.nIncidents} inc · worst {t.worstSeat}
              </span>
              <span
                className="w-12 text-right font-bold tabular-nums"
                style={{ color: availColor(t.avg) }}
              >
                {t.avg}%
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Fault hotspots — click a seat to inspect it. */}
      {hotspots.length > 0 && (
        <section>
          <SectionLabel>Hotspot seats · most faults in window</SectionLabel>
          <div className="flex flex-wrap gap-1">
            {hotspots.map(([sid, n]) => (
              <button
                key={sid}
                onClick={() => onSelect?.({ kind: "seat", seatId: sid })}
                className="flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 font-mono text-[11px] font-semibold text-slate-700 transition-colors hover:bg-sky-100 hover:text-sky-800"
              >
                {sid}
                <span className="rounded bg-red-100 px-1 text-[9px] font-bold text-red-600">
                  {n}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <p className="text-[10.5px] italic text-slate-400">
        Seat colours = window-average availability per seat.
      </p>
    </div>
  );
}


/** Seat headline for Site sub-mode: window average + fault count. */
export function SeatSiteCard({ site, seatId }: { site: SitePayload; seatId: string }) {
  const avg = site.seatAvgScore[seatId];
  const faults = site.seatFaultCount[seatId] ?? 0;
  if (avg === undefined) return null;
  return (
    <div className="mb-5 rounded-xl border border-slate-100 bg-gradient-to-b from-white to-slate-50 px-4 py-3 shadow-sm">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
        Site window · {site.windowDays}d · {site.tailId}
      </p>
      <div className="mt-1.5 flex items-center gap-3">
        <span
          className="text-2xl font-bold tabular-nums tracking-tight"
          style={{ color: availColor(avg) }}
        >
          {avg}%
        </span>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.min(100, avg)}%`, background: availColor(avg) }}
          />
        </div>
      </div>
      <p className="mt-1 text-[10.5px] text-slate-400">
        window-average availability ·{" "}
        {faults > 0
          ? <><b className="text-red-500">{faults}</b> fault{faults === 1 ? "" : "s"} in window</>
          : "no faults in window"}
      </p>
    </div>
  );
}


function SiteStat({
  label, value, color, onClick,
}: { label: string; value: string; color?: string; onClick?: () => void }) {
  const body = (
    <>
      <p className="text-sm font-bold tabular-nums" style={color ? { color } : undefined}>
        {value}
      </p>
      <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </p>
    </>
  );
  if (onClick) {
    return (
      <button
        onClick={onClick}
        title="Open this trip"
        className="rounded-md transition-colors hover:bg-slate-100"
      >
        {body}
      </button>
    );
  }
  return <div>{body}</div>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
      {children}
    </p>
  );
}


function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <p className="flex items-baseline justify-between gap-3 py-0.5 text-xs">
      <span className="text-slate-500">{k}</span>
      <span className="text-right font-semibold text-slate-800">{v}</span>
    </p>
  );
}


function formatTime(iso: string): string {
  return iso.replace("T", " ");
}
